import React from 'react';
import { DndContext, type DragEndEvent, type DragOverEvent } from '@dnd-kit/core';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import { makeStyles } from '@material-ui/styles';
import { colors } from '@skyslope/mache';
import update from 'immutability-helper';
import { chameleonElId, useIsSkySlopeMobileApp } from '../../utils';
import { type IGroup, type IRecipient, RecipientRole } from '../types';
import RecipientGroup from './RecipientGroup';

export const WORD_NUMBERS = [
  'First',
  'Second',
  'Third',
  'Fourth',
  'Fifth',
  'Sixth',
  'Seventh',
  'Eight',
  'Ninth',
  'Tenth',
];

const useStyles = makeStyles(() => ({
  wrapper: {
    padding: '32px 40px',
    borderLeft: `1px solid ${colors.grey[400]}`,
    overflow: 'auto',
  },
  groupHeader: {
    display: 'block',
    width: '100%',
    paddingBottom: 16,
    borderBottom: `1px solid ${colors.grey[400]}`,
  },
  plusIcon: {
    margin: '-2px 0px -2px -6px',
    height: 18,
  },
  addButton: {
    marginTop: 40,
    marginLeft: -8,
  },
}));

interface IProps {
  recipients: IRecipient[];
  signingGroups: IGroup[];
  setRecipients: (recipients: IRecipient[]) => void;
  createNewGroup: () => void;
  // modal: boolean;
  disableRecipientList?: boolean;
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const RecipientGroups = (props: IProps) => {
  const classes = useStyles();

  // const [isDropped, setIsDropped] = React.useState(false);
  const [isOver, setIsOver] = React.useState('');
  const { recipients, setRecipients, signingGroups, disableRecipientList } = props;
  const disableRecipientListClass = disableRecipientList ? '!px-4' : '';
  const isMobileApp = useIsSkySlopeMobileApp();

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const updateRecipient = (recipientId: string, groupId?: string | null) => {
    const recipientIndex = recipients.findIndex((r) => r.id === recipientId);
    let newRecipients;
    if (groupId === 'CarbonCopy') {
      // User is dragging signer to CC group, remove group, set role to CC
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      newRecipients = update(recipients, {
        [recipientIndex]: {
          $unset: ['group'],
          role: { $set: 'CarbonCopy' },
        },
      });
    } else {
      // User is dragging CC to signing group, add group and set role to Signer
      const newGroup = signingGroups.find((sg) => sg.id === groupId);
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      newRecipients = update(recipients, {
        [recipientIndex]: {
          group: { $set: newGroup },
          role: { $set: 'Signer' },
        },
      });
    }
    setRecipients(newRecipients);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleDragEnd = (e: DragEndEvent) => {
    const recipientId = e.active.id;

    // const _recipient = recipients.find((r) => r.id === recipientId);

    const groupId = e.over?.id;
    updateRecipient(recipientId as string, groupId as string);
    setIsOver('');
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleDragOver = (e: DragOverEvent) => {
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    setIsOver(e?.over?.id);
  };

  const signers = recipients.filter((r) => r.role === RecipientRole.Signer);
  const cc = recipients.filter((r) => r.role === RecipientRole.CarbonCopy);
  const sortedSigningGroups = signingGroups.sort((a, b) => a.order - b.order);
  return (
    <DndContext onDragEnd={handleDragEnd} onDragOver={handleDragOver}>
      <div
        className={`${classes.wrapper} ${disableRecipientListClass}`}
        id={chameleonElId(15)}
        style={{ paddingTop: '32px', paddingBottom: props.disableRecipientList ? '80px' : '32px' }}
      >
        <Typography variant="h6">
          {isMobileApp ? '' : props.disableRecipientList ? 'Edit Signing Order' : 'Signing order'}
        </Typography>
        <div>
          {sortedSigningGroups.map((signingGroup, i) => (
            <RecipientGroup
              key={signingGroup.id}
              groupId={signingGroup.id}
              recipients={signers.filter((r) => r.group?.id === signingGroup.id)}
              headerText={`WHO SIGNS ${WORD_NUMBERS[i]}`}
              // updateRecipient={updateRecipient}
              groupRole={RecipientRole.Signer}
              // modal={modal}
              isOver={isOver === signingGroup.id}
              disableRecipientList={disableRecipientList}
            />
          ))}
        </div>
        {signingGroups.length < Math.min(4, recipients.length) ? (
          <Button variant="text" color="primary" onClick={props.createNewGroup} className={classes.addButton}>
            <AddIcon className={classes.plusIcon} />
            Add a Signer Group
          </Button>
        ) : (
          ''
        )}

        <RecipientGroup
          recipients={cc}
          headerText="RECEIVES A COPY OF SIGNED DOCUMENTS"
          // updateRecipient={updateRecipient}
          groupRole={RecipientRole.CarbonCopy}
          // modal={modal}
          isOver={isOver === 'CarbonCopy'}
          disableRecipientList={disableRecipientList}
        />
      </div>
    </DndContext>
  );
};

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default RecipientGroups;
